import { supabase } from '@/lib/supabase';
import { Voucher, CreateVoucherDTO, UpdateVoucherDTO } from '@/types/voucher';
import { formatShortMonthName } from '@/lib/utils';

export const voucherService = {
  async getVouchers(): Promise<Voucher[]> {
    const { data, error } = await supabase
      .from('vouchers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getVouchersSecure(): Promise<Omit<Voucher, 'code'>[]> {
    const { data, error } = await supabase
      .from('vouchers')
      .select('id, expires_at, claimed_date, exam, subscription_id, status, created_at, updated_at, created_by, updated_by, issued_to')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getVoucherByCode(code: string): Promise<Voucher | null> {
    const { data, error } = await supabase
      .from('vouchers')
      .select('*')
      .eq('code', code)
      .single();

    if (error) throw error;
    return data;
  },

  async createVoucher(voucher: CreateVoucherDTO): Promise<Voucher> {
    const { data, error } = await supabase
      .from('vouchers')
      .insert([voucher])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateVoucher(id: string, updates: UpdateVoucherDTO): Promise<Voucher> {
    const { data, error } = await supabase
      .from('vouchers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async claimVoucher(code: string, userId: string): Promise<Voucher> {
    const { data: voucher, error: fetchError } = await supabase
      .from('vouchers')
      .select('*')
      .eq('code', code)
      .eq('status', 'active')
      .single();

    if (fetchError) throw fetchError;
    if (!voucher) throw new Error('Voucher não encontrado ou já utilizado');

    const now = new Date().toISOString();
    if (voucher.expires_at && new Date(voucher.expires_at) < new Date()) {
      throw new Error('Voucher expirado');
    }

    const updates: UpdateVoucherDTO = {
      status: 'claimed',
      claimed_date: now,
      updated_by: userId
    };

    const { data, error } = await supabase
      .from('vouchers')
      .update(updates)
      .eq('id', voucher.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async cancelVoucher(id: string, userId: string): Promise<Voucher> {
    const updates: UpdateVoucherDTO = {
      status: 'cancelled',
      updated_by: userId
    };

    const { data, error } = await supabase
      .from('vouchers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getVoucherStats() {
    const { data, error } = await supabase
      .from('vouchers')
      .select('status')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      total: data.length,
      active: data.filter(v => v.status === 'active').length,
      claimed: data.filter(v => v.status === 'claimed').length,
      cancelled: data.filter(v => v.status === 'cancelled').length
    };
  },

  async getMonthlyStats() {
    const { data, error } = await supabase
      .from('vouchers')
      .select('created_at')
      .order('created_at', { ascending: true });

    if (error) throw error;

    const monthlyData = new Array(12).fill(0).map((_, index) => ({
      name: formatShortMonthName(index),
      total: 0
    }));

    data.forEach(voucher => {
      const date = new Date(voucher.created_at);
      const monthIndex = date.getMonth();
      monthlyData[monthIndex].total += 1;
    });

    return monthlyData;
  }
}; 